import type {
  AgentRuntimeActionAvailability,
  AgentRuntimeActionState,
  AgentRuntimeCapabilities,
  AgentRuntimeInteractionRequest,
  AgentRuntimeMessageAttachment,
  AgentRuntimeProviderRef,
  AgentRuntimeSnapshot,
  AgentRuntimeStatus,
  AgentRuntimeTimelineItem,
  AgentRuntimeUsageState,
  AgentRuntimeUsageTotals,
} from "@cord/shared"
import {
  isAllowedImageMime,
  normalizeAgentErrorDetails,
  normalizeAttachmentMime,
  normalizeClaudeAskUserInput,
} from "@cord/shared"
import { isAbortLikeError } from "@/lib/abort-ux"
import { sanitizeClaudeRuntimeSnapshot } from "@/lib/claude-runtime-snapshot-sanitization"
import type {
  NormalizedRawSyncEvent,
  RawSyncEventBatchApplyResult,
} from "@/lib/runtime-sync-batch"
import { extractHiddenMessageLinkage } from "@/lib/session-context-transport"
import { extractRuntimeClientMessageId } from "@/lib/runtime-client-message-id"

type ClaudeEventEntry = {
  id: number
  eventType: string
  data: unknown
  timestamp: number
}

type ClaudeTimelineItemIndexState = {
  itemIndexById: Record<string, number>
  toolCallIndexByKey: Record<string, number>
  lastTextLikeIndexByKey: Record<string, number>
}

type ClaudeDerivedProjection = ClaudeTimelineItemIndexState & {
  version: number
  timelineItems: AgentRuntimeTimelineItem[]
  currentStreamingId: string | null
  currentStreamingIndex: number | null
  currentStreamingKind: "assistant_text" | "thinking" | null
  currentRunStartedAt: number | null
  inferredRunResponseStartedAt: number | null
  pendingTopLevelUserOrderingAnchorAt: number | null
  latestUsage: AgentRuntimeUsageState
}

export type ClaudeRawProjection = {
  events: ClaudeEventEntry[]
  compactedEventCount?: number
  pendingPermissionsById: Record<string, Record<string, unknown>>
  providerStatus: Record<string, unknown> | null
  providerSyncBoundaryId: number | null
  claudeDerived?: ClaudeDerivedProjection
}

// Bump this when Claude derived projection semantics change so persisted raw
// projections rebuild from their event history exactly once before catchup.
const CLAUDE_DERIVED_PROJECTION_VERSION = 2

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null
  return value.length > 0 ? value : null
}

function normalizeTimestamp(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null
  const normalized = Math.floor(value)
  return normalized < 10_000_000_000 ? normalized * 1000 : normalized
}

function normalizeTokenValue(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0
  return Math.max(0, value)
}

function resolveTimestamp(...values: unknown[]): number {
  for (const value of values) {
    const normalized = normalizeTimestamp(value)
    if (normalized !== null) return normalized
  }
  return Date.now()
}

function createAvailability(enabled: boolean, disabledReason: string | null): AgentRuntimeActionAvailability {
  return {
    available: true,
    enabled,
    disabledReason: enabled ? null : disabledReason,
  }
}

function buildProviderRef(fields: Omit<AgentRuntimeProviderRef, "agentType">): AgentRuntimeProviderRef | undefined {
  if (
    !fields.sessionId &&
    !fields.messageId &&
    !fields.toolCallId &&
    !fields.childSessionId &&
    !fields.parentToolCallId
  ) {
    return undefined
  }

  return {
    agentType: "claude_code",
    ...fields,
  }
}

function extractActorMetadata(raw: unknown): AgentRuntimeTimelineItem["actor"] {
  const source = asRecord(raw)
  if (!source) return undefined

  const actorRaw = asRecord(source.cordActor) ?? source
  const userId = asString(actorRaw.userId)
  const name = asString(actorRaw.name)
  const email = asString(actorRaw.email)
  const orgRole =
    actorRaw.orgRole === "owner" || actorRaw.orgRole === "admin" || actorRaw.orgRole === "member"
      ? actorRaw.orgRole
      : null
  const sourceType = actorRaw.source === "session_ws" || actorRaw.source === "remote_surface"
    ? actorRaw.source
    : null
  const submittedAt = normalizeTimestamp(actorRaw.submittedAt)

  if (!userId && !name && !email && !orgRole && !sourceType && submittedAt === null) {
    return undefined
  }

  return {
    userId,
    name,
    email,
    orgRole,
    source: sourceType,
    submittedAt,
  }
}

function stringifyContent(content: unknown): string {
  if (typeof content === "string") return content
  if (Array.isArray(content)) {
    return content
      .map((item) => {
        if (typeof item === "string") return item
        const record = asRecord(item)
        if (!record) return ""
        if (typeof record.text === "string") return record.text
        if (record.type === "image") return "[image]"
        return JSON.stringify(record)
      })
      .filter(Boolean)
      .join("\n")
  }

  const record = asRecord(content)
  if (record) {
    if (typeof record.text === "string") return record.text
    return JSON.stringify(record, null, 2)
  }

  return ""
}

function buildRuntimeAttachment(input: {
  id: string
  url: string
  filename?: string | null
  mime?: string | null
}): AgentRuntimeMessageAttachment {
  return {
    id: input.id,
    url: input.url,
    filename: input.filename ?? undefined,
    mime: input.mime ?? undefined,
  }
}

// This fallback is only for preserving inline previews when the durable R2
// attachment rewrite is unavailable. Keep it well below upload limits so raw
// history/projection snapshots do not cache multi-megabyte data URLs; larger
// attachments should resolve through the R2 metadata path instead.
const CLAUDE_FALLBACK_DATA_URL_MAX_SOURCE_CHARS = 1_500_000

function isAllowedClaudeBase64AttachmentMime(mime: string): boolean {
  const normalizedMime = normalizeAttachmentMime(mime)
  return isAllowedImageMime(normalizedMime) || normalizedMime === "application/pdf"
}

function isAllowedClaudeTextAttachmentMime(mime: string): boolean {
  return normalizeAttachmentMime(mime) === "text/plain"
}

function resolveClaudeAttachmentUrl(input: {
  record: Record<string, unknown>
  source: Record<string, unknown> | null
}): string | null {
  const directUrl = asString(input.record.url) ?? asString(input.source?.url)
  if (directUrl) return directUrl

  const sourceType = asString(input.source?.type)
  const sourceData = asNonEmptyString(input.source?.data)
  const mediaType =
    asString(input.record.mime) ??
    asString(input.record.media_type) ??
    asString(input.source?.mime) ??
    asString(input.source?.media_type)
  if (!sourceData || !mediaType || sourceData.length > CLAUDE_FALLBACK_DATA_URL_MAX_SOURCE_CHARS) {
    return null
  }

  if (sourceType === "base64") {
    if (!isAllowedClaudeBase64AttachmentMime(mediaType)) return null
    return `data:${normalizeAttachmentMime(mediaType)};base64,${sourceData}`
  }

  if (sourceType === "text") {
    if (!isAllowedClaudeTextAttachmentMime(mediaType)) return null
    return `data:${normalizeAttachmentMime(mediaType)};charset=utf-8,${encodeURIComponent(
      sourceData,
    )}`
  }

  return null
}

function resolveClaudeParentToolCallId(message: Record<string, unknown>): string | null {
  const topLevel =
    asString(message.parent_tool_use_id) ??
    asString(message.parentToolUseId) ??
    asString(message.parent_tool_call_id) ??
    asString(message.parentToolCallId)
  if (topLevel) return topLevel

  const metadata = asRecord(message.metadata)
  return (
    asString(metadata?.parent_tool_use_id) ??
    asString(metadata?.parentToolUseId) ??
    asString(metadata?.parent_tool_call_id) ??
    asString(metadata?.parentToolCallId)
  )
}

function extractClaudeToolUseResult(
  payload: unknown,
  parentToolCallId: string | null,
): { toolUseId: string; content: string; isError: boolean } | null {
  const record = asRecord(payload)
  const toolUseId =
    asString(record?.tool_use_id) ??
    asString(record?.toolUseId) ??
    asString(record?.tool_useID) ??
    parentToolCallId
  if (!toolUseId) return null

  const contentSource = record?.content ?? record?.result ?? record?.output ?? record?.text ?? payload
  const content = stringifyContent(contentSource).trim()
  const isError =
    record?.is_error === true ||
    record?.isError === true ||
    (typeof record?.error === "string" && record.error.trim().length > 0)

  return {
    toolUseId,
    content: content || "(empty result)",
    isError,
  }
}

function extractClaudeErrorDetails(event: Record<string, unknown>): {
  message: string
  type: string | null
  code: string | null
} {
  const normalized = normalizeAgentErrorDetails({
    value: {
      error: event.error,
      message: event.message,
      code: event.code,
      errorCode: event.errorCode,
    },
    fallbackMessage: "Claude run failed",
    fallbackType: "runtime_error",
  })

  return {
    message: normalized?.message ?? "Claude run failed",
    type: normalized?.type ?? null,
    code: normalized?.code ?? null,
  }
}

function extractClaudeErrorMessage(event: Record<string, unknown>): string {
  return extractClaudeErrorDetails(event).message
}

function sessionActionLabel(action: unknown): string | null {
  if (action === "undo") return "Undo complete"
  if (action === "redo") return "Redo complete"
  if (action === "summarize" || action === "compact") return "Context compaction requested"
  return null
}

const CLAUDE_INTERRUPTED_BY_USER_STATUS = "[Request interrupted by user]"
// Claude can emit duplicate synthetic status echoes in the same short turn; a
// five-second wall-clock window matches the existing UI de-dupe tolerance while
// avoiding suppression of later, intentionally repeated statuses.
const CLAUDE_RECENT_STATUS_DEDUPE_WINDOW_MS = 5_000

function isClaudeInterruptedByUserStatus(args: { text: string }): boolean {
  return args.text.trim() === CLAUDE_INTERRUPTED_BY_USER_STATUS
}

function sortClaudeTimelineItems(items: AgentRuntimeTimelineItem[]): AgentRuntimeTimelineItem[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      if (left.item.timestamp !== right.item.timestamp) {
        return left.item.timestamp - right.item.timestamp
      }
      return left.index - right.index
    })
    .map((entry) => entry.item)
}

function createEmptyClaudeDerivedProjection(): ClaudeDerivedProjection {
  return {
    version: CLAUDE_DERIVED_PROJECTION_VERSION,
    timelineItems: [],
    currentStreamingId: null,
    currentStreamingIndex: null,
    currentStreamingKind: null,
    currentRunStartedAt: null,
    inferredRunResponseStartedAt: null,
    pendingTopLevelUserOrderingAnchorAt: null,
    itemIndexById: {},
    toolCallIndexByKey: {},
    lastTextLikeIndexByKey: {},
    latestUsage: {},
  }
}

function buildClaudeToolCallIndexKey(args: {
  toolCallId: string
  parentToolCallId: string | null
}): string {
  return `${args.parentToolCallId ?? ""}\u0000${args.toolCallId}`
}

function buildClaudeToolTimelineItemId(args: {
  toolCallId: string
  parentToolCallId: string | null
}): string {
  if (!args.parentToolCallId) return `claude:tool:${args.toolCallId}`
  return `claude:tool:${args.parentToolCallId}:${args.toolCallId}`
}

function buildClaudeTextLikeIndexKey(args: {
  kind: "assistant_text" | "thinking"
  parentToolCallId: string | null
}): string {
  return `${args.kind}\u0000${args.parentToolCallId ?? ""}`
}

function reindexClaudeTimelineItemsMutable(state: ClaudeTimelineItemIndexState & {
  timelineItems: AgentRuntimeTimelineItem[]
}): void {
  state.itemIndexById = {}
  state.toolCallIndexByKey = {}
  state.lastTextLikeIndexByKey = {}

  for (const [index, item] of state.timelineItems.entries()) {
    state.itemIndexById[item.id] = index
    if (item.kind === "tool_call") {
      state.toolCallIndexByKey[buildClaudeToolCallIndexKey({
        toolCallId: item.toolCallId,
        parentToolCallId: item.parentToolCallId ?? null,
      })] = index
      continue
    }
    if (item.kind === "assistant_text" || item.kind === "thinking") {
      state.lastTextLikeIndexByKey[buildClaudeTextLikeIndexKey({
        kind: item.kind,
        parentToolCallId: item.providerRef?.parentToolCallId ?? null,
      })] = index
    }
  }
}

function deleteClaudeTimelineItemIndexesMutable(
  state: ClaudeTimelineItemIndexState,
  item: AgentRuntimeTimelineItem | undefined,
): void {
  if (!item) return
  delete state.itemIndexById[item.id]
  if (item.kind === "tool_call") {
    delete state.toolCallIndexByKey[buildClaudeToolCallIndexKey({
      toolCallId: item.toolCallId,
      parentToolCallId: item.parentToolCallId ?? null,
    })]
    return
  }
  if (item.kind === "assistant_text" || item.kind === "thinking") {
    delete state.lastTextLikeIndexByKey[buildClaudeTextLikeIndexKey({
      kind: item.kind,
      parentToolCallId: item.providerRef?.parentToolCallId ?? null,
    })]
  }
}

function indexClaudeTimelineItemMutable(
  state: ClaudeTimelineItemIndexState,
  item: AgentRuntimeTimelineItem,
  index: number,
): void {
  state.itemIndexById[item.id] = index
  if (item.kind === "tool_call") {
    state.toolCallIndexByKey[buildClaudeToolCallIndexKey({
      toolCallId: item.toolCallId,
      parentToolCallId: item.parentToolCallId ?? null,
    })] = index
    return
  }
  if (item.kind === "assistant_text" || item.kind === "thinking") {
    state.lastTextLikeIndexByKey[buildClaudeTextLikeIndexKey({
      kind: item.kind,
      parentToolCallId: item.providerRef?.parentToolCallId ?? null,
    })] = index
  }
}

function pushClaudeTimelineItemMutable(
  state: ClaudeTimelineItemIndexState & { timelineItems: AgentRuntimeTimelineItem[] },
  item: AgentRuntimeTimelineItem,
): void {
  state.timelineItems.push(item)
  indexClaudeTimelineItemMutable(state, item, state.timelineItems.length - 1)
}

function replaceClaudeTimelineItemMutable(
  state: ClaudeTimelineItemIndexState & { timelineItems: AgentRuntimeTimelineItem[] },
  index: number,
  item: AgentRuntimeTimelineItem,
): void {
  deleteClaudeTimelineItemIndexesMutable(state, state.timelineItems[index])
  state.timelineItems[index] = item
  indexClaudeTimelineItemMutable(state, item, index)
}

function extractClaudeRevertTargetUserMessageId(event: Record<string, unknown>): string | null {
  return asString(event.targetUserMessageId) ?? asString(event.target_user_message_id)
}

function updateClaudeToolCallMutable(
  target: AgentRuntimeTimelineItem[] | (ClaudeTimelineItemIndexState & { timelineItems: AgentRuntimeTimelineItem[] }),
  input: {
    toolCallId: string
    content: string
    isError: boolean
    timestamp: number
    parentToolCallId: string | null
  },
): void {
  const state = Array.isArray(target)
    ? { timelineItems: target, itemIndexById: {}, toolCallIndexByKey: {}, lastTextLikeIndexByKey: {} }
    : target
  if (Array.isArray(target)) reindexClaudeTimelineItemsMutable(state)
  const items = state.timelineItems
  const index = state.toolCallIndexByKey[buildClaudeToolCallIndexKey({
    toolCallId: input.toolCallId,
    parentToolCallId: input.parentToolCallId,
  })] ?? -1

  if (index >= 0) {
    const current = items[index]
    if (current.kind === "tool_call") {
      replaceClaudeTimelineItemMutable(state, index, {
        ...current,
        output: input.content,
        error: input.isError ? input.content : null,
        status: input.isError ? "error" : "completed",
      })
    }
    return
  }

  pushClaudeTimelineItemMutable(state, {
    id: buildClaudeToolTimelineItemId({
      toolCallId: input.toolCallId,
      parentToolCallId: input.parentToolCallId,
    }),
    kind: "tool_call",
    toolCallId: input.toolCallId,
    toolName: "Tool",
    input: {},
    output: input.content,
    error: input.isError ? input.content : null,
    status: input.isError ? "error" : "completed",
    timestamp: input.timestamp,
    parentToolCallId: input.parentToolCallId ?? undefined,
    providerRef: buildProviderRef({
      toolCallId: input.toolCallId,
      parentToolCallId: input.parentToolCallId ?? undefined,
    }),
  })
}

function upsertClaudeToolUseMutable(
  target: AgentRuntimeTimelineItem[] | (ClaudeTimelineItemIndexState & { timelineItems: AgentRuntimeTimelineItem[] }),
  input: {
    messageId: string
    toolCallId: string
    toolName: string
    toolInput: unknown
    timestamp: number
    parentToolCallId: string | null
  },
): void {
  const state = Array.isArray(target)
    ? { timelineItems: target, itemIndexById: {}, toolCallIndexByKey: {}, lastTextLikeIndexByKey: {} }
    : target
  if (Array.isArray(target)) reindexClaudeTimelineItemsMutable(state)
  const items = state.timelineItems
  const existingIndex = state.toolCallIndexByKey[buildClaudeToolCallIndexKey({
    toolCallId: input.toolCallId,
    parentToolCallId: input.parentToolCallId,
  })] ?? -1

  if (existingIndex >= 0) {
    const current = items[existingIndex]
    if (current.kind === "tool_call") {
      replaceClaudeTimelineItemMutable(state, existingIndex, {
        ...current,
        id: buildClaudeToolTimelineItemId({
          toolCallId: input.toolCallId,
          parentToolCallId: input.parentToolCallId,
        }),
        messageId: current.messageId ?? input.messageId,
        toolName: current.toolName === "Tool" ? input.toolName : current.toolName,
        input: current.input && Object.keys(asRecord(current.input) ?? {}).length > 0 ? current.input : input.toolInput,
        timestamp: Math.min(current.timestamp, input.timestamp),
        parentToolCallId: current.parentToolCallId ?? input.parentToolCallId ?? undefined,
        providerRef: buildProviderRef({
          messageId: current.messageId ?? input.messageId,
          toolCallId: input.toolCallId,
          parentToolCallId: current.parentToolCallId ?? input.parentToolCallId ?? undefined,
        }),
      })
    }
    return
  }

  pushClaudeTimelineItemMutable(state, {
    id: buildClaudeToolTimelineItemId({
      toolCallId: input.toolCallId,
      parentToolCallId: input.parentToolCallId,
    }),
    kind: "tool_call",
    messageId: input.messageId,
    toolCallId: input.toolCallId,
    toolName: input.toolName,
    input: input.toolInput,
    status: "running",
    timestamp: input.timestamp,
    parentToolCallId: input.parentToolCallId ?? undefined,
    providerRef: buildProviderRef({
      messageId: input.messageId,
      toolCallId: input.toolCallId,
      parentToolCallId: input.parentToolCallId ?? undefined,
    }),
  })
}

function shouldMergeText(existing: string, next: string): boolean {
  if (!existing || !next) return false
  if (existing === next) return true
  if (next.startsWith(existing)) return true
  if (existing.startsWith(next)) return true
  return false
}

function findLastClaudeTextLikeItemIndex(
  target: AgentRuntimeTimelineItem[] | ClaudeTimelineItemIndexState,
  kind: "assistant_text" | "thinking",
  parentToolCallId: string | null,
): number {
  if (Array.isArray(target)) {
    for (let index = target.length - 1; index >= 0; index -= 1) {
      const item = target[index]
      if (item.kind !== kind) continue
      const itemParentToolCallId = item.providerRef?.parentToolCallId ?? null
      if (itemParentToolCallId !== parentToolCallId) continue
      return index
    }
    return -1
  }
  const state = target
  return state.lastTextLikeIndexByKey[buildClaudeTextLikeIndexKey({ kind, parentToolCallId })] ?? -1
}

function buildClaudeTimeline(events: ClaudeEventEntry[]): AgentRuntimeTimelineItem[] {
  return buildClaudeSnapshotTimelineFromDerived({
    derived: buildClaudeDerivedProjectionFromEvents(events),
  })
}

export function closeClaudeStreamingTimelineItems(args: {
  timeline: AgentRuntimeTimelineItem[]
  statusPhase: AgentRuntimeStatus["phase"]
}): AgentRuntimeTimelineItem[] {
  if (args.statusPhase === "running") return args.timeline

  let changed = false
  const nextTimeline = args.timeline.map((item) => {
    if (
      (item.kind === "assistant_text" || item.kind === "thinking") &&
      item.streaming
    ) {
      changed = true
      return {
        ...item,
        streaming: false,
      }
    }

    return item
  })

  return changed ? nextTimeline : args.timeline
}

function normalizeProviderStatus(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === "string") return { type: raw }
  const record = asRecord(raw)
  return record ? { ...record } : null
}

function inferProviderStatusFromEvents(events: ClaudeEventEntry[]): Record<string, unknown> | null {
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const entry = events[index]
    switch (entry.eventType) {
      case "run_error": {
        const record = asRecord(entry.data)
        return {
          type: "error",
          message: extractClaudeErrorMessage(record ?? {}),
          finishedAt: entry.timestamp,
          finishReason: "error",
        }
      }
      case "run_completed":
        return { type: "idle", finishedAt: entry.timestamp, finishReason: "completed" }
      case "aborted":
        return { type: "idle", finishedAt: entry.timestamp, finishReason: "aborted" }
      case "run_started":
        return { type: "busy", startedAt: entry.timestamp }
      default:
        break
    }
  }
  return null
}

function buildPendingInteractions(
  projection: ClaudeRawProjection,
): AgentRuntimeInteractionRequest[] {
  const interactions: AgentRuntimeInteractionRequest[] = []

  for (const permission of Object.values(projection.pendingPermissionsById)) {
    const requestId = asString(permission.requestId) ?? asString(permission.id)
    if (!requestId) continue

    const toolName = asString(permission.toolName) ?? undefined
    if (toolName === "AskQuestion" || toolName === "AskUserQuestion") {
      const askUser = normalizeClaudeAskUserInput({ input: permission.input, requestId })
      interactions.push({
        kind: "ask_user",
        requestId,
        title: askUser?.title ?? undefined,
        prompts: askUser?.questions ?? [],
        raw: permission,
        providerRef: buildProviderRef({
          sessionId: asString(permission.sessionId) ?? undefined,
        }),
      })
      continue
    }

    interactions.push({
      kind: "permission",
      requestId,
      permission: asString(permission.permission) ?? undefined,
      toolName,
      input: permission.input,
      raw: permission,
      providerRef: buildProviderRef({
        sessionId: asString(permission.sessionId) ?? undefined,
      }),
    })
  }

  return interactions
}

function buildStatus(
  providerStatus: Record<string, unknown> | null,
  pendingInteractionCount: number,
): AgentRuntimeStatus {
  const rawType = asString(providerStatus?.type)?.toLowerCase() ?? "idle"
  const errorDetails = extractClaudeErrorDetails(providerStatus ?? {})
  let phase: AgentRuntimeStatus["phase"] = "idle"
  if (rawType === "error" || rawType === "failed") {
    phase = "error"
  } else if (rawType === "waiting_input" || pendingInteractionCount > 0) {
    phase = "waiting_input"
  } else if (rawType === "busy" || rawType === "running" || rawType === "retry" || rawType === "working") {
    phase = "running"
  }

  return {
    phase,
    agentType: "claude_code",
    startedAt: normalizeTimestamp(providerStatus?.startedAt),
    finishedAt: normalizeTimestamp(providerStatus?.finishedAt),
    finishReason:
      asString(providerStatus?.finishReason) ??
      (phase === "waiting_input" ? "waiting_input" : phase === "idle" ? "completed" : null),
    error:
      phase === "error"
        ? {
            type:
              asString(providerStatus?.errorType) ??
              asString(providerStatus?.errorCode) ??
              errorDetails.type ??
              errorDetails.code ??
              "runtime_error",
            message: errorDetails.message,
          }
        : null,
  }
}

function buildActions(
  status: AgentRuntimeStatus,
  timeline: AgentRuntimeTimelineItem[],
  pendingInteractions: AgentRuntimeInteractionRequest[],
): AgentRuntimeActionState {
  const busy = status.phase === "running"
  const hasConversation = timeline.length > 0
  const hasPendingPermission = pendingInteractions.some(
    (entry) => entry.kind === "permission" || entry.kind === "ask_user",
  )
  const busyReason = "Unavailable while agent is running"
  const noConversationReason = "No conversation yet"

  return {
    availability: {
      abort: createAvailability(busy, "Nothing to abort right now"),
      undo: createAvailability(!busy && hasConversation, busy ? busyReason : noConversationReason),
      redo: createAvailability(false, "No redo checkpoint available"),
      compact: createAvailability(!busy, busy ? busyReason : null),
      fork_message: createAvailability(!busy && hasConversation, busy ? busyReason : noConversationReason),
      edit_message: createAvailability(!busy && hasConversation, busy ? busyReason : noConversationReason),
      reply_permission: createAvailability(hasPendingPermission, "No pending permission request"),
      reply_question: createAvailability(false, "No pending question"),
      question_reject: createAvailability(false, "No pending question"),
    },
    revertMessageId: null,
  }
}

function buildCapabilities(): AgentRuntimeCapabilities {
  return {
    agentType: "claude_code",
    supportsPromptParts: true,
    supportsImageAttachments: true,
    supportsSlashCommands: true,
    supportsSessionActions: true,
    supportsQuestions: false,
    supportsPermissions: true,
    supportsUsage: true,
    supportsForkFromMessage: true,
    supportsEditFromMessage: true,
    supportsSubagents: true,
  }
}

function buildUsageTotalsFromClaudeUsage(raw: unknown): AgentRuntimeUsageTotals | null {
  const usage = asRecord(raw)
  if (!usage) return null

  const inputTokens = normalizeTokenValue(usage.input_tokens ?? usage.inputTokens)
  const outputTokens = normalizeTokenValue(usage.output_tokens ?? usage.outputTokens)
  const reasoningTokens = normalizeTokenValue(usage.reasoning_tokens ?? usage.reasoningTokens)
  const cacheReadTokens = normalizeTokenValue(
    usage.cache_read_input_tokens ?? usage.cacheReadInputTokens,
  )
  const cacheWriteTokens = normalizeTokenValue(
    usage.cache_creation_input_tokens ?? usage.cacheCreationInputTokens,
  )

  const computedUsedTokens =
    inputTokens + outputTokens + reasoningTokens + cacheReadTokens + cacheWriteTokens
  const usedTokens = Math.max(
    normalizeTokenValue(usage.used_tokens ?? usage.usedTokens),
    computedUsedTokens,
  )
  if (usedTokens <= 0) return null

  return {
    inputTokens,
    outputTokens,
    reasoningTokens,
    cacheReadTokens,
    cacheWriteTokens,
    usedTokens,
  }
}

function cloneClaudeDerivedProjection(input: ClaudeDerivedProjection): ClaudeDerivedProjection {
  const latestUsage: AgentRuntimeUsageState = {}
  if (input.latestUsage.latestTurn) {
    latestUsage.latestTurn = { ...input.latestUsage.latestTurn }
  }
  if (input.latestUsage.currentContext) {
    latestUsage.currentContext = { ...input.latestUsage.currentContext }
  }

  return {
    version: input.version,
    timelineItems: [...input.timelineItems],
    currentStreamingId: input.currentStreamingId,
    currentStreamingIndex: input.currentStreamingIndex,
    currentStreamingKind: input.currentStreamingKind,
    currentRunStartedAt: input.currentRunStartedAt,
    inferredRunResponseStartedAt: input.inferredRunResponseStartedAt,
    pendingTopLevelUserOrderingAnchorAt: input.pendingTopLevelUserOrderingAnchorAt,
    itemIndexById: { ...input.itemIndexById },
    toolCallIndexByKey: { ...input.toolCallIndexByKey },
    lastTextLikeIndexByKey: { ...input.lastTextLikeIndexByKey },
    latestUsage,
  }
}

export function isCurrentClaudeDerivedProjection(value: ClaudeRawProjection["claudeDerived"]): value is ClaudeDerivedProjection {
  return Boolean(
    value &&
    value.version === CLAUDE_DERIVED_PROJECTION_VERSION &&
    Array.isArray(value.timelineItems) &&
    value.itemIndexById &&
    value.toolCallIndexByKey &&
    value.lastTextLikeIndexByKey,
  )
}

function finalizeClaudeStreamingMutable(state: ClaudeDerivedProjection): void {
  if (state.currentStreamingIndex !== null) {
    const current = state.timelineItems[state.currentStreamingIndex]
    if (current && (current.kind === "assistant_text" || current.kind === "thinking")) {
      replaceClaudeTimelineItemMutable(state, state.currentStreamingIndex, {
        ...current,
        streaming: false,
      })
    }
  }
  state.currentStreamingId = null
  state.currentStreamingIndex = null
  state.currentStreamingKind = null
}

function clearClaudeCurrentRunOrderingAnchorsMutable(state: ClaudeDerivedProjection): void {
  state.currentRunStartedAt = null
  state.inferredRunResponseStartedAt = null
}

function clearAllClaudeTopLevelUserOrderingAnchorsMutable(state: ClaudeDerivedProjection): void {
  clearClaudeCurrentRunOrderingAnchorsMutable(state)
  state.pendingTopLevelUserOrderingAnchorAt = null
}

function updateClaudePendingTopLevelUserOrderingAnchorMutable(
  state: ClaudeDerivedProjection,
  timestamp: number,
): void {
  if (!Number.isFinite(timestamp)) return
  if (
    state.pendingTopLevelUserOrderingAnchorAt === null ||
    timestamp < state.pendingTopLevelUserOrderingAnchorAt
  ) {
    state.pendingTopLevelUserOrderingAnchorAt = timestamp
  }
}

function markClaudeRunResponseStartedMutable(
  state: ClaudeDerivedProjection,
  timestamp: number,
): void {
  if (state.currentRunStartedAt !== null) return
  if (state.inferredRunResponseStartedAt === null || timestamp < state.inferredRunResponseStartedAt) {
    state.inferredRunResponseStartedAt = timestamp
  }
  updateClaudePendingTopLevelUserOrderingAnchorMutable(state, timestamp)
}

function updateClaudeUsageFromMessageMutable(args: {
  state: ClaudeDerivedProjection
  message: Record<string, unknown>
}): void {
  const parentToolCallId = resolveClaudeParentToolCallId(args.message)
  if (parentToolCallId) return

  const usage = buildUsageTotalsFromClaudeUsage(
    asRecord(args.message.message)?.usage ?? args.message.usage,
  )
  if (!usage) return

  args.state.latestUsage = {
    latestTurn: usage,
    currentContext: usage,
  }
}

function applyClaudeTimelineEntryMutable(args: {
  state: ClaudeDerivedProjection
  entry: ClaudeEventEntry
  entryIndex: number
}): void {
  const { state, entry, entryIndex } = args
  const items = state.timelineItems
  const event = asRecord(entry.data)
  if (!event) return

  const eventType = asString(event.type) ?? entry.eventType
  if (!eventType) return

  const eventTimestamp = resolveTimestamp(event.timestamp, entry.timestamp)

  if (eventType === "run_started") {
    state.currentRunStartedAt = eventTimestamp
    state.inferredRunResponseStartedAt = null
    state.pendingTopLevelUserOrderingAnchorAt = eventTimestamp
    return
  }

  if (eventType === "run_completed" || eventType === "aborted") {
    finalizeClaudeStreamingMutable(state)
    clearAllClaudeTopLevelUserOrderingAnchorsMutable(state)
    return
  }

  if (eventType === "permission_request") {
    finalizeClaudeStreamingMutable(state)
    clearClaudeCurrentRunOrderingAnchorsMutable(state)
    return
  }

  if (eventType === "run_error") {
    finalizeClaudeStreamingMutable(state)
    clearAllClaudeTopLevelUserOrderingAnchorsMutable(state)
    const errorDetails = extractClaudeErrorDetails(event)
    if (isAbortLikeError({ type: errorDetails.code ?? errorDetails.type, message: errorDetails.message })) {
      pushClaudeTimelineItemMutable(state, {
        id: `claude:status:${eventTimestamp}:${entryIndex}`,
        kind: "marker",
        markerType: "status",
        label: "Response stopped",
        timestamp: eventTimestamp,
      })
    } else {
      pushClaudeTimelineItemMutable(state, {
        id: `claude:error:${eventTimestamp}:${entryIndex}`,
        kind: "error",
        message: errorDetails.message,
        code: errorDetails.code ?? errorDetails.type ?? undefined,
        timestamp: eventTimestamp,
      })
    }
    return
  }

  if (eventType === "session_action") {
    const action = asString(event.action)
    if (action === "revert_to_message") {
      const replacementTargetUserMessageId = extractClaudeRevertTargetUserMessageId(event)
      if (replacementTargetUserMessageId) {
        const sortedItems = sortClaudeTimelineItems(items)
        const replacementTargetIndex = sortedItems.findIndex((item) => (
          item.kind === "user_message" &&
          item.providerRef?.messageId === replacementTargetUserMessageId
        ))
        if (replacementTargetIndex >= 0) {
          state.timelineItems = sortedItems.slice(0, replacementTargetIndex)
          reindexClaudeTimelineItemsMutable(state)
        }
      }
      state.currentStreamingId = null
      state.currentStreamingIndex = null
      state.currentStreamingKind = null
      clearAllClaudeTopLevelUserOrderingAnchorsMutable(state)
      return
    }

    const label = sessionActionLabel(event.action)
    if (label) {
      pushClaudeTimelineItemMutable(state, {
        id: `claude:session-action:${eventTimestamp}:${entryIndex}`,
        kind: "marker",
        markerType: "session_action",
        label,
        timestamp: eventTimestamp,
      })
    }
    return
  }

  if (eventType !== "sdk_message") return

  const message = asRecord(event.message)
  if (!message) return
  const messageType = asString(message.type)
  if (!messageType) return
  const messageTimestamp = resolveTimestamp(message.timestamp, eventTimestamp)
  const parentToolCallId = resolveClaudeParentToolCallId(message)

  if (messageType === "system") {
    if (message.subtype === "compact_boundary") {
      const compactMeta = asRecord(message.compact_metadata)
      const trigger = asString(compactMeta?.trigger) ?? asString(message.trigger) ?? "automatic"
      pushClaudeTimelineItemMutable(state, {
        id: `claude:context-boundary:${messageTimestamp}:${entryIndex}`,
        kind: "marker",
        markerType: "context_boundary",
        label: `Context compacted - ${trigger}`,
        timestamp: messageTimestamp,
      })
    }
    return
  }

  if (messageType === "stream_event") {
    const rawEvent = asRecord(message.event)
    const rawEventType = asString(rawEvent?.type)
    if (!rawEventType) return

    if (rawEventType === "content_block_start") {
      const block = asRecord(rawEvent?.content_block)
      const blockType = asString(block?.type) ?? "text"
      state.currentStreamingKind = blockType === "thinking" || blockType === "redacted_thinking"
        ? "thinking"
        : "assistant_text"
      return
    }

    if (rawEventType === "content_block_delta") {
      const delta = asRecord(rawEvent?.delta)
      const deltaText = asNonEmptyString(delta?.text) ?? asNonEmptyString(delta?.thinking)
      const isThinkingDelta = delta?.type === "thinking_delta" || delta?.type === "redacted_thinking_delta"
      if (!deltaText) return

      markClaudeRunResponseStartedMutable(state, messageTimestamp)

      const kind: "assistant_text" | "thinking" =
        state.currentStreamingKind === "thinking" || isThinkingDelta ? "thinking" : "assistant_text"
      const streamId: string = state.currentStreamingId ?? `claude:stream:${messageTimestamp}:${entryIndex}`
      if (!state.currentStreamingId) {
        pushClaudeTimelineItemMutable(state, {
          id: streamId,
          kind,
          text: deltaText,
          streaming: true,
          timestamp: messageTimestamp,
          providerRef: buildProviderRef({
            parentToolCallId: parentToolCallId ?? undefined,
          }),
        })
        state.currentStreamingIndex = state.timelineItems.length - 1
      } else {
        const streamingIndex = state.currentStreamingIndex
        const current = streamingIndex !== null ? state.timelineItems[streamingIndex] : null
        if (streamingIndex !== null && current && (current.kind === "assistant_text" || current.kind === "thinking")) {
          replaceClaudeTimelineItemMutable(state, streamingIndex, {
            ...current,
            text: current.text + deltaText,
            streaming: true,
          })
        }
      }

      state.currentStreamingId = streamId
      state.currentStreamingKind = kind
      return
    }

    if (rawEventType === "content_block_stop" || rawEventType === "message_stop") {
      finalizeClaudeStreamingMutable(state)
    }
    return
  }

  if (messageType === "assistant") {
    markClaudeRunResponseStartedMutable(state, messageTimestamp)
    updateClaudeUsageFromMessageMutable({ state, message })

    const container = asRecord(message.message)
    const content = Array.isArray(message.message)
      ? message.message
      : Array.isArray(container?.content)
        ? container.content
        : []
    const messageId = asString(message.uuid) ?? `claude:assistant:${messageTimestamp}:${entryIndex}`
    const actor = extractActorMetadata(message.metadata)

    const textBlocks = content
      .map((block) => asRecord(block))
      .filter((block): block is Record<string, unknown> => block !== null && block.type === "text")
      .map((block) => typeof block.text === "string" ? block.text : "")
      .filter((value) => value.length > 0)

    const thinkingBlocks = content
      .map((block) => asRecord(block))
      .filter((block): block is Record<string, unknown> => {
        if (!block) return false
        return block.type === "thinking" || block.type === "redacted_thinking"
      })
      .map((block) => asString(block.thinking) ?? asString(block.text) ?? "")
      .filter((value) => value.length > 0)

    if (textBlocks.length > 0) {
      const text = textBlocks.join("\n").trimEnd()
      const currentIndex = state.currentStreamingKind === "assistant_text" && state.currentStreamingIndex !== null
        ? state.currentStreamingIndex
        : -1
      let mergedIntoExisting = false
      if (currentIndex >= 0) {
        const current = state.timelineItems[currentIndex]
        if (current.kind === "assistant_text") {
          replaceClaudeTimelineItemMutable(state, currentIndex, {
            ...current,
            id: messageId,
            messageId,
            text,
            streaming: false,
            actor: current.actor ?? actor,
            providerRef: buildProviderRef({
              messageId,
              parentToolCallId: parentToolCallId ?? undefined,
            }),
          })
          mergedIntoExisting = true
        }
      }

      if (!mergedIntoExisting) {
        const lastIndex = findLastClaudeTextLikeItemIndex(state, "assistant_text", parentToolCallId)
        if (lastIndex >= 0) {
          const current = state.timelineItems[lastIndex]
          if (
            current.kind === "assistant_text" &&
            ((messageId && current.messageId === messageId) || shouldMergeText(current.text, text))
          ) {
            replaceClaudeTimelineItemMutable(state, lastIndex, {
              ...current,
              id: messageId,
              messageId,
              text,
              streaming: false,
              actor: current.actor ?? actor,
              providerRef: buildProviderRef({
                messageId,
                parentToolCallId: parentToolCallId ?? undefined,
              }),
            })
            mergedIntoExisting = true
          }
        }
      }

      if (!mergedIntoExisting) {
        pushClaudeTimelineItemMutable(state, {
          id: messageId,
          kind: "assistant_text",
          messageId,
          text,
          streaming: false,
          timestamp: messageTimestamp,
          actor,
          providerRef: buildProviderRef({
            messageId,
            parentToolCallId: parentToolCallId ?? undefined,
          }),
        })
      }
    }

    if (thinkingBlocks.length > 0) {
      const text = thinkingBlocks.join("\n").trimEnd()
      const currentIndex = state.currentStreamingKind === "thinking" && state.currentStreamingIndex !== null
        ? state.currentStreamingIndex
        : -1
      let mergedIntoExisting = false
      if (currentIndex >= 0) {
        const current = state.timelineItems[currentIndex]
        if (current.kind === "thinking") {
          replaceClaudeTimelineItemMutable(state, currentIndex, {
            ...current,
            id: `claude:thinking:${messageId}`,
            messageId,
            text,
            streaming: false,
            providerRef: buildProviderRef({
              messageId,
              parentToolCallId: parentToolCallId ?? undefined,
            }),
          })
          mergedIntoExisting = true
        }
      }

      if (!mergedIntoExisting) {
        const lastIndex = findLastClaudeTextLikeItemIndex(state, "thinking", parentToolCallId)
        if (lastIndex >= 0) {
          const current = state.timelineItems[lastIndex]
          if (
            current.kind === "thinking" &&
            ((messageId && current.messageId === messageId) || shouldMergeText(current.text, text))
          ) {
            replaceClaudeTimelineItemMutable(state, lastIndex, {
              ...current,
              id: `claude:thinking:${messageId}`,
              messageId,
              text,
              streaming: false,
              providerRef: buildProviderRef({
                messageId,
                parentToolCallId: parentToolCallId ?? undefined,
              }),
            })
            mergedIntoExisting = true
          }
        }
      }

      if (!mergedIntoExisting) {
        pushClaudeTimelineItemMutable(state, {
          id: `claude:thinking:${messageId}`,
          kind: "thinking",
          messageId,
          text,
          streaming: false,
          timestamp: messageTimestamp,
          providerRef: buildProviderRef({
            messageId,
            parentToolCallId: parentToolCallId ?? undefined,
          }),
        })
      }
    }

    for (const block of content) {
      const toolUse = asRecord(block)
      if (!toolUse || toolUse.type !== "tool_use") continue
      const toolCallId = asString(toolUse.id) ?? `claude:tool:${messageTimestamp}:${entryIndex}`
      upsertClaudeToolUseMutable(state, {
        messageId,
        toolCallId,
        toolName: asString(toolUse.name) ?? "Tool",
        toolInput: toolUse.input ?? {},
        timestamp: messageTimestamp,
        parentToolCallId,
      })
    }

    finalizeClaudeStreamingMutable(state)
    return
  }

  if (messageType === "user") {
    const container = asRecord(message.message)
    const content = Array.isArray(message.message)
      ? message.message
      : Array.isArray(container?.content)
        ? container.content
        : []
    const messageId = asString(message.uuid) ?? `claude:user:${messageTimestamp}:${entryIndex}`
    const clientMessageId = extractRuntimeClientMessageId({ value: message })
    const isSynthetic = message.isSynthetic === true || message.is_synthetic === true
    const actorFromMessage = extractActorMetadata(message.metadata)
    const topLevelToolResult = extractClaudeToolUseResult(
      message.tool_use_result ?? message.toolUseResult ?? null,
      parentToolCallId,
    )
    if (topLevelToolResult) {
      updateClaudeToolCallMutable(state, {
        toolCallId: topLevelToolResult.toolUseId,
        content: topLevelToolResult.content,
        isError: topLevelToolResult.isError,
        timestamp: messageTimestamp,
        parentToolCallId,
      })
    }

    const textSegments: string[] = []
    const rawTextSegments: string[] = []
    let attachmentCount = 0
    const attachments: AgentRuntimeMessageAttachment[] = []
    let actor = actorFromMessage
    let hasToolResultBlock = false
    const contextRefs = new Map<string, { referenceId?: string; sessionId: string; repositoryId?: string | null; title?: string | null }>()

    for (const block of content) {
      const record = asRecord(block)
      if (!record) continue

      if (record.type === "tool_result") {
        hasToolResultBlock = true
        const normalizedResult = extractClaudeToolUseResult(record, parentToolCallId)
        if (!normalizedResult) continue
        updateClaudeToolCallMutable(state, {
          toolCallId: normalizedResult.toolUseId,
          content: normalizedResult.content,
          isError: normalizedResult.isError,
          timestamp: messageTimestamp,
          parentToolCallId,
        })
        continue
      }

      if (record.type === "image" || record.type === "document") {
        attachmentCount += 1
        const source = asRecord(record.source)
        const attachmentUrl = resolveClaudeAttachmentUrl({ record, source })
        if (attachmentUrl) {
          attachments.push(buildRuntimeAttachment({
            id: asString(record.id) ?? `${messageId}:attachment:${attachmentCount}`,
            url: attachmentUrl,
            filename: asString(record.filename) ?? asString(record.title),
            mime:
              asString(record.mime) ??
              asString(record.media_type) ??
              asString(source?.mime) ??
              asString(source?.media_type),
          }))
        }
        continue
      }

      if (record.type === "text") {
        actor ??= extractActorMetadata(record.metadata)
        const blockText = typeof record.text === "string" ? record.text : ""
        if (blockText.length > 0) {
          rawTextSegments.push(blockText)
          textSegments.push(blockText)
        }
      }
    }

    const extractedContext = extractHiddenMessageLinkage(rawTextSegments.join("\n"))
    for (const ref of extractedContext.contextRefs) {
      contextRefs.set(ref.referenceId ?? ref.sessionId, ref)
    }

    if (parentToolCallId || topLevelToolResult || hasToolResultBlock) return

    const visibleMessageLinkage = extractHiddenMessageLinkage(textSegments.join("\n"))
    const text = visibleMessageLinkage.text.trimEnd()
    if (isClaudeInterruptedByUserStatus({ text })) return

    if (isSynthetic) {
      if (text.length > 0) {
        const lastItem = state.timelineItems[state.timelineItems.length - 1]
        const isDuplicateRecentStatus =
          Boolean(lastItem) &&
          lastItem?.kind === "marker" &&
          lastItem.markerType === "status" &&
          lastItem.label === text &&
          Math.abs(messageTimestamp - lastItem.timestamp) < CLAUDE_RECENT_STATUS_DEDUPE_WINDOW_MS
        if (!isDuplicateRecentStatus) {
          pushClaudeTimelineItemMutable(state, {
            id: `claude:status:${messageTimestamp}:${entryIndex}`,
            kind: "marker",
            markerType: "status",
            label: text,
            timestamp: messageTimestamp,
          })
        }
      }
      return
    }

    if (
      text.length === 0
      && attachmentCount === 0
      && contextRefs.size === 0
      && extractedContext.requestedSkillRefs.length === 0
      && extractedContext.preferredMcpServerRefs.length === 0
      && !extractedContext.attachedPullRequest
    ) {
      return
    }

    const userOrderingAnchorTimestamp =
      state.currentRunStartedAt ??
      state.inferredRunResponseStartedAt ??
      state.pendingTopLevelUserOrderingAnchorAt
    const adjustedUserTimestamp = userOrderingAnchorTimestamp !== null
      ? Math.min(messageTimestamp, Math.max(1, userOrderingAnchorTimestamp - 1))
      : messageTimestamp

    pushClaudeTimelineItemMutable(state, {
      id: messageId,
      kind: "user_message",
      clientMessageId: visibleMessageLinkage.clientMessageId ?? extractedContext.clientMessageId ?? clientMessageId,
      text,
      attachmentCount: attachmentCount > 0 ? attachmentCount : undefined,
      attachments: attachments.length > 0 ? attachments : undefined,
      contextRefs: contextRefs.size > 0 ? Array.from(contextRefs.values()) : undefined,
      requestedSkillRefs:
        extractedContext.requestedSkillRefs.length > 0
          ? extractedContext.requestedSkillRefs
          : undefined,
      preferredMcpServerRefs:
        extractedContext.preferredMcpServerRefs.length > 0
          ? extractedContext.preferredMcpServerRefs
          : undefined,
      attachedPullRequest: extractedContext.attachedPullRequest,
      timestamp: adjustedUserTimestamp,
      actor,
      providerRef: buildProviderRef({
        messageId,
        parentToolCallId: parentToolCallId ?? undefined,
      }),
    })
    state.pendingTopLevelUserOrderingAnchorAt = null
  }
}

function buildClaudeDerivedProjectionFromEvents(events: ClaudeEventEntry[]): ClaudeDerivedProjection {
  const state = createEmptyClaudeDerivedProjection()
  for (const [entryIndex, entry] of events.entries()) {
    applyClaudeTimelineEntryMutable({ state, entry, entryIndex })
  }
  return state
}

function buildClaudeSnapshotTimelineFromDerived(args: {
  derived: ClaudeDerivedProjection
}): AgentRuntimeTimelineItem[] {
  const timelineItems = [...args.derived.timelineItems]
  const streamingIndex = args.derived.currentStreamingIndex
  const streamingItem = streamingIndex !== null ? timelineItems[streamingIndex] : null
  if (streamingIndex !== null && streamingItem && (streamingItem.kind === "assistant_text" || streamingItem.kind === "thinking")) {
    timelineItems[streamingIndex] = {
      ...streamingItem,
      streaming: false,
    }
  }
  return sortClaudeTimelineItems(timelineItems)
}

function cloneProjectionForMutation(input: ClaudeRawProjection): ClaudeRawProjection {
  const claudeDerived = isCurrentClaudeDerivedProjection(input.claudeDerived)
    ? cloneClaudeDerivedProjection(input.claudeDerived)
    : buildClaudeDerivedProjectionFromEvents(input.events)

  return {
    // Event payloads are treated as immutable history records after ingest, so
    // batch apply only needs fresh top-level containers here. If a future
    // change starts mutating nested payload/status objects in place, this helper
    // must return to deep cloning those specific structures.
    events: [...input.events],
    compactedEventCount: Number.isFinite(input.compactedEventCount)
      ? Math.max(0, Math.floor(input.compactedEventCount ?? 0))
      : undefined,
    pendingPermissionsById: { ...input.pendingPermissionsById },
    providerStatus: input.providerStatus ? { ...input.providerStatus } : null,
    providerSyncBoundaryId: input.providerSyncBoundaryId,
    claudeDerived,
  }
}

export function stripClaudeDerivedProjectionForPersistence(
  projection: ClaudeRawProjection | null,
): ClaudeRawProjection | null {
  if (!projection || !projection.claudeDerived) return projection
  if (
    typeof projection.compactedEventCount === "number" &&
    Number.isFinite(projection.compactedEventCount) &&
    projection.compactedEventCount > 0
  ) {
    return projection
  }

  const { claudeDerived: _claudeDerived, ...strippedProjection } = projection
  return strippedProjection
}

export function compactClaudeProjectionForRuntimeCheckpoint(
  projection: ClaudeRawProjection,
): ClaudeRawProjection {
  const eventCount = projection.events.length
  const existingCompactedEventCount =
    typeof projection.compactedEventCount === "number" && Number.isFinite(projection.compactedEventCount)
      ? Math.max(0, Math.floor(projection.compactedEventCount))
      : 0
  const claudeDerived = isCurrentClaudeDerivedProjection(projection.claudeDerived)
    ? projection.claudeDerived
    : buildClaudeDerivedProjectionFromEvents(projection.events)

  return {
    events: [],
    compactedEventCount: existingCompactedEventCount + eventCount,
    pendingPermissionsById: { ...projection.pendingPermissionsById },
    providerStatus: projection.providerStatus ? { ...projection.providerStatus } : null,
    providerSyncBoundaryId: projection.providerSyncBoundaryId,
    claudeDerived,
  }
}

const CLAUDE_INCREMENTAL_EVENT_TYPES = new Set([
  "sdk_message",
  "run_started",
  "run_completed",
  "run_error",
  "permission_request",
  "permission_reply",
  "session_action",
  "aborted",
])

function applyIncrementalEventMutable(
  projection: ClaudeRawProjection,
  eventType: string,
  payload: Record<string, unknown>,
  eventId: number,
  timestamp: number,
): void {
  const entry = {
    id: eventId,
    eventType,
    data: payload,
    timestamp,
  }
  projection.events.push(entry)
  if (!isCurrentClaudeDerivedProjection(projection.claudeDerived)) {
    projection.claudeDerived = buildClaudeDerivedProjectionFromEvents(projection.events.slice(0, -1))
  }
  const compactedEventCount =
    typeof projection.compactedEventCount === "number" && Number.isFinite(projection.compactedEventCount)
      ? Math.max(0, Math.floor(projection.compactedEventCount))
      : 0
  applyClaudeTimelineEntryMutable({
    state: projection.claudeDerived,
    entry,
    entryIndex: compactedEventCount + projection.events.length - 1,
  })

  switch (eventType) {
    case "run_started": {
      projection.pendingPermissionsById = {}
      projection.providerStatus = {
        type: "busy",
        startedAt: timestamp,
      }
      break
    }
    case "run_completed": {
      projection.pendingPermissionsById = {}
      projection.providerStatus = {
        type: "idle",
        finishedAt: timestamp,
        finishReason: "completed",
      }
      break
    }
    case "aborted": {
      projection.pendingPermissionsById = {}
      projection.providerStatus = {
        type: "idle",
        finishedAt: timestamp,
        finishReason: "aborted",
      }
      break
    }
    case "run_error": {
      const errorDetails = extractClaudeErrorDetails(payload)
      projection.pendingPermissionsById = {}
      projection.providerStatus = {
        type: "error",
        finishedAt: timestamp,
        finishReason: "error",
        message: errorDetails.message,
        errorType: errorDetails.type,
        errorCode: errorDetails.code,
      }
      break
    }
    case "permission_request": {
      const requestId = asString(payload.requestId) ?? asString(payload.id)
      if (requestId) {
        projection.pendingPermissionsById[requestId] = { ...payload }
      }
      const startedAt =
        typeof projection.providerStatus?.startedAt === "number" && Number.isFinite(projection.providerStatus.startedAt)
          ? projection.providerStatus.startedAt
          : timestamp
      projection.providerStatus = {
        type: "waiting_input",
        startedAt,
        finishedAt: timestamp,
        finishReason: "waiting_input",
      }
      break
    }
    case "permission_reply": {
      const requestId = asString(payload.requestId) ?? asString(payload.requestID) ?? asString(payload.id)
      if (requestId) {
        delete projection.pendingPermissionsById[requestId]
      }
      if (Object.keys(projection.pendingPermissionsById).length === 0) {
        const startedAt =
          typeof projection.providerStatus?.startedAt === "number" && Number.isFinite(projection.providerStatus.startedAt)
            ? projection.providerStatus.startedAt
            : timestamp
        projection.providerStatus = {
          type: "busy",
          startedAt,
        }
      }
      break
    }
    case "session_action": {
      const action = asString(payload.action)
      if (action === "revert_to_message") {
        projection.pendingPermissionsById = {}
        projection.providerStatus = {
          type: "idle",
          finishedAt: timestamp,
          finishReason: "revert_to_message",
        }
      }
      break
    }
    default:
      break
  }
}

function applyIncrementalEvent(
  projection: ClaudeRawProjection,
  eventType: string,
  payload: Record<string, unknown>,
  eventId: number,
  timestamp: number,
): ClaudeRawProjection {
  const next = cloneProjectionForMutation(projection)
  applyIncrementalEventMutable(next, eventType, payload, eventId, timestamp)
  return next
}

export function buildClaudeSnapshot(
  projection: ClaudeRawProjection,
  eventId: number,
): AgentRuntimeSnapshot {
  const derived = isCurrentClaudeDerivedProjection(projection.claudeDerived)
    ? projection.claudeDerived
    : buildClaudeDerivedProjectionFromEvents(projection.events)
  const rawTimeline = buildClaudeSnapshotTimelineFromDerived({ derived })
  const pendingInteractions = buildPendingInteractions(projection)
  const status = buildStatus(projection.providerStatus, pendingInteractions.length)
  const timeline = closeClaudeStreamingTimelineItems({
    timeline: rawTimeline,
    statusPhase: status.phase,
  })
  const usage = derived.latestUsage

  return sanitizeClaudeRuntimeSnapshot({
    snapshot: {
      timeline,
      pendingMessages: [],
      pendingInteractions,
      status,
      usage,
      actions: buildActions(status, timeline, pendingInteractions),
      activeEditOperation: null,
      conversationEpoch: 0,
      sessionActionOutcome: null,
      capabilities: buildCapabilities(),
      cursorState: {
        providerEventId: eventId,
        canonicalEventId: null,
        providerSyncBoundaryId: projection.providerSyncBoundaryId,
      },
    },
  }) as AgentRuntimeSnapshot
}

export function createEmptyClaudeProjection(): ClaudeRawProjection {
  return {
    events: [],
    pendingPermissionsById: {},
    providerStatus: null,
    providerSyncBoundaryId: null,
    claudeDerived: createEmptyClaudeDerivedProjection(),
  }
}

export function applyClaudeBaselineSyncEventsBatch(input: {
  projection: ClaudeRawProjection | null
  events: Array<{
    baselineSeq: number
    eventType: string
    payload: unknown
    timestamp: number
  }>
}): RawSyncEventBatchApplyResult<ClaudeRawProjection> | null {
  const baseProjection = input.projection ?? createEmptyClaudeProjection()
  const existingBaselineEventIds = new Set<number>()
  for (const entry of baseProjection.events) {
    if (typeof entry.id === "number" && Number.isFinite(entry.id) && entry.id < 0) {
      existingBaselineEventIds.add(entry.id)
    }
  }

  const sortedEvents = [...input.events].sort((left, right) => left.baselineSeq - right.baselineSeq)
  let nextProjection = baseProjection
  let appliedCount = 0
  let lastAppliedEventId = 0
  let lastAppliedTimestamp = 0
  let lastAcceptedBaselineSeq: number | null = null
  let mutatedProjection: ClaudeRawProjection | null = null

  for (const event of sortedEvents) {
    const baselineSeq = Number.isFinite(event.baselineSeq) && event.baselineSeq > 0
      ? Math.floor(event.baselineSeq)
      : 0
    if (baselineSeq <= 0) continue
    if (lastAcceptedBaselineSeq !== null && baselineSeq <= lastAcceptedBaselineSeq) continue

    const syntheticEventId = -baselineSeq
    lastAcceptedBaselineSeq = baselineSeq
    if (existingBaselineEventIds.has(syntheticEventId)) continue
    if (!CLAUDE_INCREMENTAL_EVENT_TYPES.has(event.eventType)) continue

    const payload = asRecord(event.payload)
    if (!payload) continue

    if (mutatedProjection === null) {
      mutatedProjection = cloneProjectionForMutation(baseProjection)
      nextProjection = mutatedProjection
    }
    applyIncrementalEventMutable(
      mutatedProjection,
      event.eventType,
      payload,
      syntheticEventId,
      event.timestamp,
    )
    existingBaselineEventIds.add(syntheticEventId)
    appliedCount += 1
    lastAppliedEventId = syntheticEventId
    lastAppliedTimestamp = event.timestamp
  }

  if (appliedCount === 0) return null

  return {
    projection: nextProjection,
    snapshot: buildClaudeSnapshot(nextProjection, lastAppliedEventId),
    appliedCount,
    lastAppliedEventId,
    lastAppliedTimestamp,
  }
}

export function applyClaudeRawSyncEvent(input: {
  projection: ClaudeRawProjection | null
  eventType: string
  payload: unknown
  eventId: number
  timestamp: number
}): { projection: ClaudeRawProjection; snapshot: AgentRuntimeSnapshot } | null {
  const eventId = Number.isFinite(input.eventId) && input.eventId > 0
    ? Math.floor(input.eventId)
    : 0
  if (eventId <= 0) return null

  if (!CLAUDE_INCREMENTAL_EVENT_TYPES.has(input.eventType)) return null
  const payload = asRecord(input.payload)
  if (!payload) return null

  const nextProjection = applyIncrementalEvent(
    input.projection ?? createEmptyClaudeProjection(),
    input.eventType,
    payload,
    eventId,
    input.timestamp,
  )

  return {
    projection: nextProjection,
    snapshot: buildClaudeSnapshot(nextProjection, eventId),
  }
}

export function applyClaudeRawSyncEventsBatch(input: {
  projection: ClaudeRawProjection | null
  events: NormalizedRawSyncEvent[]
}): RawSyncEventBatchApplyResult<ClaudeRawProjection> | null {
  const baseProjection = input.projection ?? createEmptyClaudeProjection()
  let nextProjection = baseProjection
  let appliedCount = 0
  let lastAppliedEventId = 0
  let lastAppliedTimestamp = 0
  let mutatedProjection: ClaudeRawProjection | null = null

  for (const event of input.events) {
    const eventId = Number.isFinite(event.eventId) && event.eventId > 0
      ? Math.floor(event.eventId)
      : 0
    if (eventId <= 0) continue
    if (!CLAUDE_INCREMENTAL_EVENT_TYPES.has(event.eventType)) continue

    const payload = asRecord(event.payload)
    if (!payload) continue

    if (mutatedProjection === null) {
      mutatedProjection = cloneProjectionForMutation(baseProjection)
      nextProjection = mutatedProjection
    }
    applyIncrementalEventMutable(
      mutatedProjection,
      event.eventType,
      payload,
      eventId,
      event.timestamp,
    )
    appliedCount += 1
    lastAppliedEventId = eventId
    lastAppliedTimestamp = event.timestamp
  }

  if (appliedCount === 0) return null

  return {
    projection: nextProjection,
    snapshot: buildClaudeSnapshot(nextProjection, lastAppliedEventId),
    appliedCount,
    lastAppliedEventId,
    lastAppliedTimestamp,
  }
}
